import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { BusinessContextService } from '../services/business-context.service';
import { LocalStorageService } from '../services/storage.service';
import { ConfigService } from '../services/config.service';
import { KeycloakService } from 'keycloak-angular';

@Injectable()
export class BusinessHttpInterceptor implements HttpInterceptor {
  constructor(
    private bizCtx: BusinessContextService,
    private ls: LocalStorageService,
    private cfg: ConfigService,
    private keycloak: KeycloakService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log(`[BizInterceptor] Intercepting request to: ${req.url}`, req.method);

    // Skip config file requests and non-API requests
    if (req.url.includes('config.json') || !req.url.includes('/api/')) {
      console.log(`[BizInterceptor] Skipping non-API request: ${req.url}`);
      return next.handle(req);
    }

    // No enviar X-Business-Uuid en /auth/me: debe hidratar contexto sin tenant
    const isAuthMe = /\/auth\/me(\?|$)/.test((req.url || '').toString());

    // Helper: intentar extraer businessUuid desde la URL de la API
    const extractBusinessFromUrl = (url: string): string | null => {
      try {
        const full = (url || '').toString();
        const clean = full.split('?')[0];
        const parts = clean.split('/').filter(Boolean);
        const tokens = ['order', 'category', 'portions', 'category-portion', 'voucher', 'tables', 'order-detail', 'menu-product', 'payment'];
        for (const t of tokens) {
          const idx = parts.findIndex(p => p === t);
          if (idx > -1 && parts.length > idx + 1) {
            const candidate = parts[idx + 1];
            // evitar palabras reservadas que no son businessUuid
            if (['flows', 'ready', 'pending', 'sent', 'by-business-order', 'process', 'statuses'].includes(candidate)) continue;
            const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
            const hashRegex = /^[0-9a-zA-Z_-]{12,}$/;
            if (uuidRegex.test(candidate) || hashRegex.test(candidate)) {
              return candidate;
            }
          }
        }
        // fallback: buscar cualquier segmento que parezca UUID
        const anyUuid = parts.find(p => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(p));
        return anyUuid || null;
      } catch {
        return null;
      }
    };

    // Get the access token from Keycloak (defensive: service may be uninitialized)
    let token: string | undefined;
    let tokenParsed: any = undefined;
    try {
      const inst: any = (this.keycloak && typeof (this.keycloak as any).getKeycloakInstance === 'function')
        ? (this.keycloak as any).getKeycloakInstance()
        : null;
      token = inst?.token as string | undefined;
      tokenParsed = inst?.tokenParsed as any;
      // If we have a parsed token, try to derive and persist realm override
      if (tokenParsed) {
        try {
          (this.cfg as any).maybeSetRealmFromTokenParsed?.(tokenParsed);
        } catch { }
      }
    } catch (e) {
      console.warn('[BizInterceptor] Keycloak instance not ready; proceeding without token');
    }
    if (!token) {
      // Do NOT stop the chain; KeycloakBearerInterceptor or backend might allow public endpoints
      console.warn('[BizInterceptor] No access token available');
    }

    // Resolve business UUID from multiple sources
    const fromCtx = this.bizCtx.getSelected();
    const fromLsRaw = this.ls.getItem<string>('ACTIVE_BUSINESS_UUID');
    // Sanear posibles comillas provenientes de localStorage (JSON.stringify)
    const fromLs = typeof fromLsRaw === 'string'
      ? fromLsRaw.replace(/^\s*["']+|["']+\s*$/g, '').trim()
      : fromLsRaw as any;
    const fromTokenActive = tokenParsed?.activeBusinessUuid as string | undefined;
    const fromTokenSingle = tokenParsed?.businessUuid as string | undefined;
    const fromTokenListFirst = Array.isArray(tokenParsed?.businessUuids) && tokenParsed.businessUuids.length > 0
      ? tokenParsed.businessUuids[0]
      : undefined;
    // Extraer de claims 'groups' con formato business::<uuid>
    const groupsArr: string[] = Array.isArray(tokenParsed?.groups) ? tokenParsed.groups.map((g: any) => (g || '').toString()) : [];
    const fromGroups = (() => {
      for (const g of groupsArr) {
        const m = g.match(/^business::([^\s]+)$/i);
        if (m && m[1]) return m[1];
      }
      return undefined;
    })();
    // Extraer lista permitida desde roles del cliente: resource_access.easy-store-front.roles
    const clientRoles: string[] = Array.isArray(tokenParsed?.resource_access?.['easy-store-front']?.roles)
      ? tokenParsed.resource_access['easy-store-front'].roles.map((r: any) => (r || '').toString())
      : [];
    const allowedFromRoles: string[] = clientRoles
      .map((r: string) => {
        const m = r.match(/^business::(.+)$/);
        return m ? m[1] : null;
      })
      .filter((v: string | null): v is string => !!v)
      .map((s: string) => s.replace(/^\s*["']+|["']+\s*$/g, '').trim());
    const tokenBusinessList: string[] = Array.isArray(tokenParsed?.businessUuids)
      ? tokenParsed.businessUuids.map((u: any) => (u || '').toString().replace(/^\s*["']+|["']+\s*$/g, '').trim())
      : [];
    const allowedSet = new Set<string>([...allowedFromRoles, ...tokenBusinessList].filter((s: string) => !!s));
    const fromConfig = (this.cfg as any)?.customer as string | undefined;
    const existingHeader = (req.headers.get('X-Business-Uuid') || '').toString().trim();
    const fromUrl = extractBusinessFromUrl(req.url) || '';
    // Orden de preferencia: header existente > derivado de URL > contexto/LS/token/config
    const businessUuid = (existingHeader || fromUrl || fromCtx || fromLs || fromTokenActive || fromTokenSingle || fromTokenListFirst || fromGroups || fromConfig || '')
      .toString()
      .replace(/^\s*["']+|["']+\s*$/g, '')
      .trim();
    // Debug: log roles present in the token to diagnose 403
    try {
      const realmRoles = Array.isArray(tokenParsed?.realm_access?.roles) ? tokenParsed.realm_access.roles : [];
      const clientRoles = tokenParsed?.resource_access?.['easy-store-front']?.roles || [];
      console.log('[BizInterceptor][DEBUG] Roles in token => realm:', realmRoles, 'client(easy-store-front):', clientRoles);
      console.log('[BizInterceptor][DEBUG] Business resolution =>', { fromCtx, fromLs, fromTokenActive, fromTokenSingle, fromTokenListFirst, fromGroups, fromConfig, chosen: businessUuid });
    } catch { }
    // Persistir solo si el UUID está permitido
    try {
      if (businessUuid && allowedSet.has(businessUuid)) {
        if (businessUuid !== fromCtx) this.bizCtx.setSelected(businessUuid);
        if (businessUuid !== fromLs) this.ls.setItem('ACTIVE_BUSINESS_UUID', businessUuid);
      }
    } catch { }

    // Create new headers with the required authentication and business UUID
    let newHeaders = req.headers
      .set('X-Kc-Client-Id', 'easy-store-front');

    // Header de negocio: solo si está permitido por el token, y nunca para /auth/me
    if (!isAuthMe) {
      const candidateExisting = existingHeader && allowedSet.has(existingHeader) ? existingHeader : '';
      const candidateResolved = businessUuid && allowedSet.has(businessUuid) ? businessUuid : '';
      const chosen = (candidateExisting || candidateResolved);
      if (chosen && chosen.length > 0) {
        newHeaders = newHeaders.set('X-Business-Uuid', chosen);
      }
    }

    if (token) {
      newHeaders = newHeaders.set('Authorization', `Bearer ${token}`);
    }

    // Proveer X-Realm si está disponible (sin comillas). No enviar X-Issuer.
    try {
      const issuer: string | undefined = tokenParsed?.iss;
      const realmFromToken = issuer ? (this.cfg as any).extractRealmFromIssuer?.(issuer) : undefined;
      const realm = ((this.cfg as any).keycloakRealm || realmFromToken || '').toString().replace(/^\s*["']+|["']+\s*$/g, '').trim();
      if (realm) {
        newHeaders = newHeaders.set('X-Realm', realm);
      }
    } catch { }

    // Only set Content-Type for non-GET/HEAD requests if not already present
    if (!['GET', 'HEAD'].includes(req.method) && !newHeaders.has('Content-Type')) {
      newHeaders = newHeaders.set('Content-Type', 'application/json');
    }

    // Clone the request with the new headers
    const cloned = req.clone({
      headers: newHeaders,
      withCredentials: true
    });

    // Log the request
    console.log(`[BizInterceptor] ${req.method} ${req.url}`, {
      headers: Array.from(newHeaders.keys()).reduce((acc, key) => ({
        ...acc,
        [key]: newHeaders.getAll(key)
      }), {})
    });

    console.log('[BizInterceptor] Sending request with headers:', {
      url: req.url,
      method: req.method,
      headers: Array.from(newHeaders.keys()).map(k => `${k}: ${newHeaders.get(k)?.substring(0, 10)}...`)
    });

    return next.handle(cloned).pipe(
      tap({
        next: (event: HttpEvent<any>) => console.log('[BizInterceptor] Response event:', event.type),
        error: (error: any) => console.error('[BizInterceptor] Request error:', error)
      }),
      catchError((error) => {
        // Handle 401 Unauthorized - redirect to login
        if (error.status === 401) {
          console.error('[BizInterceptor] 401 Unauthorized - logging out and redirecting to login');

          // Clear local storage
          try {
            this.ls.clear();
          } catch (e) {
            console.error('[BizInterceptor] Error clearing localStorage:', e);
          }

          // Trigger Keycloak logout which will redirect to login
          try {
            this.keycloak.logout(window.location.origin);
          } catch (e) {
            console.error('[BizInterceptor] Error during Keycloak logout:', e);
            // Fallback: redirect to login page manually
            window.location.href = '/login';
          }
        }

        // Re-throw the error so it can be handled by the calling code
        return throwError(() => error);
      })
    );
  }
}
